import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  RefreshIcon,
  MailIcon,
  CheckCircleIcon,
  AlertIcon,
  SettingsIcon,
  PlayIcon
} from '../components/common/Icons';

interface EmailConfig {
  configured: boolean;
  provider: string | null;
  provider_name: string | null;
  is_healthy: boolean | null;
  last_verified: string | null;
}

interface EmailProviderField {
  name: string;
  type: string;
  required: boolean;
  label?: string;
  description?: string;
  default?: string | number;
  placeholder?: string;
  sensitive?: boolean;
  options?: string[];
}

interface EmailProvider {
  id: string;
  name: string;
  description: string;
  production_ready: boolean;
  config_fields?: EmailProviderField[];
}

interface EmailTemplate {
  id: string;
  display_name: string;
  subject: string;
  variables: string[];
  is_default: boolean;
}

interface FullTemplate {
  id: string;
  display_name: string;
  subject: string;
  html: string;
  text: string;
  variables: string[];
  is_default: boolean;
}

interface Feedback {
  type: 'success' | 'error';
  message: string;
}

const EmailPage: React.FC = () => {
  const { authFetch, user, isLoading: authLoading } = useAuth();
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'config' | 'providers' | 'templates'>('config');
  const [error, setError] = useState<string | null>(null);

  // Provider configuration
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [configForm, setConfigForm] = useState<Record<string, string>>({});
  const [configuring, setConfiguring] = useState(false);

  // Template editor
  const [selectedTemplate, setSelectedTemplate] = useState<FullTemplate | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [editText, setEditText] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState('');
  const [saving, setSaving] = useState(false);

  // Feedback banner
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  useEffect(() => {
    if (authLoading) return;
    fetchEmailData();
  }, [authLoading]);

  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const fetchEmailData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [configRes, providersRes, templatesRes] = await Promise.all([
        authFetch('/api/admin/email/config'),
        authFetch('/api/admin/email/providers'),
        authFetch('/api/admin/email/templates')
      ]);

      if (configRes.ok) setConfig(await configRes.json());
      else {
        const d = await configRes.json().catch(() => ({}));
        setError(typeof d.detail === 'string' ? d.detail : 'Failed to fetch email config');
      }

      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(Array.isArray(data) ? data : data.providers || []);
      }

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(Array.isArray(data) ? data : data.templates || []);
      }
    } catch {
      setError('Failed to connect to API');
    }
    setIsLoading(false);
  };

  // ── Config tab handlers ──

  const handleSendTestEmail = async () => {
    try {
      const response = await authFetch('/api/admin/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: user?.email || 'admin@example.com' })
      });
      if (response.ok) {
        setFeedback({ type: 'success', message: `Test email sent to ${user?.email || 'recipient'}` });
      } else {
        const err = await response.json().catch(() => ({}));
        setFeedback({ type: 'error', message: typeof err.detail === 'string' ? err.detail : 'Failed to send test email' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to send test email' });
    }
  };

  // ── Provider tab handlers ──

  const handleSelectProvider = (providerId: string) => {
    setSelectedProvider(providerId);
    const provider = providers.find(p => p.id === providerId);
    if (provider?.config_fields?.length) {
      const defaults: Record<string, string> = {};
      provider.config_fields.forEach(f => {
        defaults[f.name] = f.default !== undefined ? String(f.default) : '';
      });
      setConfigForm(defaults);
    } else {
      setConfigForm({});
    }
  };

  const handleConfigureProvider = async () => {
    if (!selectedProvider) return;
    setConfiguring(true);
    const provider = providers.find(p => p.id === selectedProvider);
    const fields = provider?.config_fields || [];
    const submitConfig: Record<string, string> = {};
    Object.entries(configForm).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        submitConfig[key] = value;
      } else {
        const field = fields.find(f => f.name === key);
        if (field?.required) submitConfig[key] = value;
      }
    });
    try {
      const response = await authFetch('/api/admin/email/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: selectedProvider, config: submitConfig }),
      });
      if (response.ok) {
        setFeedback({ type: 'success', message: `${provider?.name || selectedProvider} configured` });
        setSelectedProvider(null);
        setActiveTab('config');
        await fetchEmailData();
      } else {
        const err = await response.json().catch(() => ({}));
        setFeedback({ type: 'error', message: typeof err.detail === 'string' ? err.detail : 'Failed to configure provider' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to configure provider' });
    }
    setConfiguring(false);
  };

  // ── Template tab handlers ──

  const handleSelectTemplate = async (templateId: string, force = false) => {
    if (!force && selectedTemplate?.id === templateId && !isCreating) {
      setSelectedTemplate(null);
      setPreviewHtml(null);
      return;
    }
    setIsCreating(false);
    try {
      const res = await authFetch(`/api/admin/email/templates/${templateId}`);
      if (res.ok) {
        const data: FullTemplate = await res.json();
        setSelectedTemplate(data);
        setEditDisplayName(data.display_name || '');
        setEditSubject(data.subject);
        setEditHtml(data.html);
        setEditText(data.text);
        setPreviewHtml(null);
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to load template' });
    }
  };

  const STARTER_HTML = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; background-color: #f5f5f5;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
        <tr>
            <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
                    <tr>
                        <td style="padding: 40px 40px 20px; text-align: center;">
                            <h1 style="margin: 0; color: #1f2937; font-size: 24px;">{{subject}}</h1>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 20px 40px;">
                            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                                Hi {{user_name}},
                            </p>
                            <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                                Your content here.
                            </p>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 30px 40px; border-top: 1px solid #e5e7eb;">
                            <p style="color: #9ca3af; font-size: 12px; text-align: center;">
                                &copy; {{year}} {{platform_name}}
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;

  const handleStartCreate = () => {
    setIsCreating(true);
    setSelectedTemplate(null);
    setNewTemplateId('');
    setEditDisplayName('New Template');
    setEditSubject('New Email');
    setEditHtml(STARTER_HTML);
    setEditText('Hi {{user_name}}, your content here.');
    setPreviewHtml(null);
  };

  const handlePreview = async () => {
    const tid = isCreating ? null : selectedTemplate?.id;
    if (!tid) {
      setFeedback({ type: 'error', message: 'Save the template first to preview' });
      return;
    }
    try {
      const res = await authFetch(`/api/admin/email/templates/${tid}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variables: {},
          // Send current editor content for live preview without saving
          subject: editSubject,
          html: editHtml,
          text: editText,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html);
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to preview template' });
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      const isNew = isCreating;
      const url = isNew ? '/api/admin/email/templates' : `/api/admin/email/templates/${selectedTemplate?.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const body = isNew
        ? { id: newTemplateId, display_name: editDisplayName, subject: editSubject, html: editHtml, text: editText }
        : { display_name: editDisplayName, subject: editSubject, html: editHtml, text: editText };

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setFeedback({ type: 'success', message: `Template ${isNew ? 'created' : 'updated'}` });
        // Optimistic update: immediately update local state so UI reflects changes
        const templateId = isNew ? newTemplateId : selectedTemplate?.id;
        if (isNew && templateId) {
          setTemplates(prev => [...prev, {
            id: templateId,
            display_name: editDisplayName || templateId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            subject: editSubject,
            variables: [],
            is_default: false,
          }]);
        } else {
          setTemplates(prev => prev.map(t =>
            t.id === templateId
              ? { ...t, display_name: editDisplayName || t.display_name, subject: editSubject }
              : t
          ));
        }
        if (isNew) setIsCreating(false);
        // Background refresh for accurate server state, then re-select
        await fetchEmailData();
        if (templateId) await handleSelectTemplate(templateId, true);
      } else {
        const err = await res.json().catch(() => ({}));
        setFeedback({ type: 'error', message: typeof err.detail === 'string' ? err.detail : 'Failed to save' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to save template' });
    }
    setSaving(false);
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate || selectedTemplate.is_default) return;
    if (!confirm(`Delete template "${selectedTemplate.id}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`/api/admin/email/templates/${selectedTemplate.id}`, { method: 'DELETE' });
      if (res.ok) {
        setFeedback({ type: 'success', message: `Template "${selectedTemplate.id}" deleted` });
        setSelectedTemplate(null);
        await fetchEmailData();
      } else {
        const err = await res.json().catch(() => ({}));
        setFeedback({ type: 'error', message: typeof err.detail === 'string' ? err.detail : 'Failed to delete' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Failed to delete template' });
    }
  };

  // ── Shared styles ──

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    padding: '10px 12px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    fontFamily: 'monospace',
    fontSize: '12px',
    resize: 'vertical',
    lineHeight: 1.5,
  };

  const varBadgeStyle: React.CSSProperties = {
    background: 'rgba(124, 58, 237, 0.2)',
    color: '#a78bfa',
    borderRadius: '4px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    display: 'inline-block',
  };

  // ── Render ──

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Email Configuration</h1>
          <p className="page-subtitle">Configure email providers and templates</p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-ghost" onClick={fetchEmailData}><RefreshIcon size={18} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        {(['config', 'providers', 'templates'] as const).map(tab => (
          <button key={tab} className={`tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'config' ? <SettingsIcon size={16} /> : <MailIcon size={16} />}
            {tab === 'config' ? 'Configuration' : tab === 'providers' ? 'Providers' : 'Templates'}
          </button>
        ))}
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          background: feedback.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${feedback.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          color: feedback.type === 'success' ? '#22c55e' : '#ef4444',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{feedback.message}</span>
          <button onClick={() => setFeedback(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '18px' }} aria-label="Dismiss">×</button>
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {isLoading ? (
        <div className="glass-card"><div className="loading-state"><div className="spinner" /><p>Loading email configuration...</p></div></div>
      ) : activeTab === 'config' ? (
        /* ═══════════════ CONFIG TAB ═══════════════ */
        <div className="grid grid-cols-2 gap-6">
          <div className="glass-card">
            <h3 className="card-title">Email Provider</h3>
            <div className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-secondary">Provider</span>
                <span className="font-medium">{config?.provider_name || config?.provider || 'Not configured'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-secondary">Status</span>
                {config?.configured
                  ? <span className="badge badge-success"><CheckCircleIcon size={14} /> Configured</span>
                  : <span className="badge badge-warning"><AlertIcon size={14} /> Not Configured</span>}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-secondary">Health</span>
                {config?.is_healthy
                  ? <span className="badge badge-success"><CheckCircleIcon size={14} /> Healthy</span>
                  : <span className="badge badge-secondary">Unknown</span>}
              </div>
              {config?.last_verified && (
                <div className="flex justify-between items-center">
                  <span className="text-secondary">Last Verified</span>
                  <span className="text-sm">{config.last_verified}</span>
                </div>
              )}
            </div>
          </div>
          <div className="glass-card">
            <h3 className="card-title">Actions</h3>
            <div className="space-y-4 mt-4">
              <button className="btn btn-secondary w-full" onClick={handleSendTestEmail}><PlayIcon size={16} /> Send Test Email</button>
              <button className="btn btn-primary w-full" onClick={() => setActiveTab('providers')}><SettingsIcon size={16} /> Configure Provider</button>
            </div>
          </div>
        </div>
      ) : activeTab === 'providers' ? (
        /* ═══════════════ PROVIDERS TAB ═══════════════ */
        <>
          <div className="glass-card">
            <h3 className="card-title mb-4">Available Email Providers</h3>
            <div className="grid grid-cols-3 gap-4">
              {providers.map(provider => {
                const pid = provider.id;
                const isSelected = selectedProvider === pid;
                return (
                  <div key={pid} className="provider-card" onClick={() => handleSelectProvider(pid)} style={{
                    cursor: 'pointer',
                    border: isSelected ? '2px solid var(--accent-primary, #3b82f6)' : '1px solid var(--glass-border, rgba(255,255,255,0.1))',
                  }}>
                    <div className="provider-icon"><MailIcon size={24} /></div>
                    <span className="font-medium">{provider.name}</span>
                    <span className="text-secondary text-sm">{provider.description}</span>
                    {provider.production_ready && <span className="badge badge-success mt-2">Production Ready</span>}
                  </div>
                );
              })}
            </div>
          </div>
          {selectedProvider && (() => {
            const provider = providers.find(p => p.id === selectedProvider);
            const fields = provider?.config_fields || [];
            return (
              <div className="glass-card" style={{ marginTop: '16px' }}>
                <h3 className="card-title">Configure {provider?.name || selectedProvider}</h3>
                <div className="space-y-4 mt-4">
                  {fields.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)' }}>No additional configuration required.</p>
                  ) : fields.map(field => (
                    <div key={field.name} style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                      <label style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                        {field.label || field.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        {field.required && <span style={{ color: '#dc2626' }}> *</span>}
                      </label>
                      {field.type === 'select' && field.options ? (
                        <select value={configForm[field.name] || ''} onChange={e => setConfigForm(p => ({ ...p, [field.name]: e.target.value }))} style={inputStyle}>
                          {field.options.map(opt => <option key={opt} value={opt} style={{ background: '#1a1a2e' }}>{opt}</option>)}
                        </select>
                      ) : (
                        <input
                          type={field.type === 'password' || field.sensitive || field.name.includes('password') || field.name.includes('secret') ? 'password' : field.type === 'email' ? 'email' : 'text'}
                          value={configForm[field.name] || ''}
                          onChange={e => setConfigForm(p => ({ ...p, [field.name]: e.target.value }))}
                          placeholder={field.placeholder || (field.default !== undefined ? String(field.default) : '')}
                          style={inputStyle}
                        />
                      )}
                      {field.description && <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{field.description}</span>}
                    </div>
                  ))}
                  {selectedProvider === 'aws_ses' && (
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
                      AWS credentials are provided automatically via IAM role on EC2.
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <button className="btn btn-primary" onClick={handleConfigureProvider} disabled={configuring}>
                      {configuring ? 'Configuring...' : 'Save & Verify'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setSelectedProvider(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      ) : (
        /* ═══════════════ TEMPLATES TAB ═══════════════ */
        <>
          <div className="glass-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 className="card-title" style={{ margin: 0 }}>Email Templates</h3>
              <button className="btn btn-primary btn-sm" onClick={handleStartCreate} style={{ fontSize: '13px', padding: '6px 14px' }}>
                + Create Template
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {templates.map(t => {
                const isSelected = selectedTemplate?.id === t.id && !isCreating;
                return (
                  <div key={t.id} className="template-card" onClick={() => handleSelectTemplate(t.id)} style={{
                    cursor: 'pointer',
                    border: isSelected ? '2px solid var(--accent-primary, #3b82f6)' : '1px solid var(--glass-border, rgba(255,255,255,0.1))',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MailIcon size={18} />
                      <span className="font-medium">{t.display_name || t.id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                    </div>
                    <span className="text-secondary text-sm" style={{ fontSize: '12px' }}>{t.subject}</span>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginTop: '4px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{t.variables?.length || 0} vars</span>
                      {t.is_default && (
                        <span style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', borderRadius: '4px' }}>Default</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '12px' }}>
              Custom templates are stored in memory and reset on service restart. Default templates are always available.
            </p>
          </div>

          {/* Template editor */}
          {(selectedTemplate || isCreating) && (
            <div className="glass-card" style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 className="card-title" style={{ margin: 0 }}>
                  {isCreating ? 'Create Template' : `Template: ${selectedTemplate?.display_name || selectedTemplate?.id}`}
                  {selectedTemplate?.is_default && (
                    <span style={{ marginLeft: '8px', fontSize: '11px', padding: '2px 8px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', borderRadius: '4px' }}>
                      Default
                    </span>
                  )}
                </h3>
                <button className="btn btn-ghost" onClick={() => { setSelectedTemplate(null); setIsCreating(false); setPreviewHtml(null); }}>
                  Close
                </button>
              </div>

              {/* Template ID (create only) */}
              {isCreating && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                    Template ID <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    value={newTemplateId}
                    onChange={e => setNewTemplateId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    placeholder="my_template_name"
                    style={{ ...inputStyle, maxWidth: '300px' }}
                  />
                  <span style={{ color: 'var(--text-muted)', fontSize: '11px', display: 'block', marginTop: '2px' }}>
                    Lowercase letters, numbers, underscores
                  </span>
                </div>
              )}

              {/* Display Name */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Display Name</label>
                <input value={editDisplayName} onChange={e => setEditDisplayName(e.target.value)} placeholder="Human-readable name" style={{ ...inputStyle, maxWidth: '400px' }} />
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Subject</label>
                <input value={editSubject} onChange={e => setEditSubject(e.target.value)} placeholder="Email subject line" style={inputStyle} />
              </div>

              {/* Variables */}
              {selectedTemplate?.variables && selectedTemplate.variables.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Variables</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedTemplate.variables.map(v => (
                      <span key={v} style={varBadgeStyle}>{'{{' + v + '}}'}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Editor + Preview split */}
              <div style={{ display: 'grid', gridTemplateColumns: previewHtml ? '1fr 1fr' : '1fr', gap: '16px', marginBottom: '12px' }}>
                <div>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>HTML</label>
                  <textarea
                    value={editHtml}
                    onChange={e => setEditHtml(e.target.value)}
                    style={{ ...textareaStyle, height: '400px' }}
                  />
                </div>
                {previewHtml && (
                  <div>
                    <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Preview</label>
                    <iframe
                      srcDoc={previewHtml}
                      style={{ width: '100%', height: '400px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: '#fff' }}
                      title="Email Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}
              </div>

              {/* Plain text */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Plain Text (optional)</label>
                <textarea value={editText} onChange={e => setEditText(e.target.value)} style={{ ...textareaStyle, height: '100px' }} />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {!isCreating && <button className="btn btn-secondary" onClick={handlePreview}>Preview</button>}
                <button className="btn btn-primary" onClick={handleSaveTemplate} disabled={saving}>
                  {saving ? 'Saving...' : isCreating ? 'Create' : 'Save'}
                </button>
                {selectedTemplate && !selectedTemplate.is_default && !isCreating && (
                  <button className="btn btn-ghost" onClick={handleDeleteTemplate} style={{ color: '#ef4444' }}>Delete</button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailPage;
